import { Spin, notification } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GdList, ItemMode } from '../../../components/GdList';
import { GdLoadingIndicator } from '../../../components/GdLoadingIndicator';
import { GdModal } from '../../../components/GdModal';
import Text from '../../../components/Text';
import { useRootStore } from '../../../context/storeContext';
import { ListContainer } from '../../Common/styles';
import { useGlossaryMeta } from '../hooks/useGlossaryMeta';
import { GlossaryWordListItem } from '../stores/glossaryStore';
import { GlossaryWordModal } from './GlossaryWordModal';

export const GlossaryList = observer(() => {
  const { t } = useTranslation();
  const { glossaryStore } = useRootStore();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleteInProgress, setDeleteInProgress] = useState(false);
  const [deletingWord, setDeletingWord] = useState<GlossaryWordListItem | null>(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditInProgress, setEditInProgress] = useState(false);
  const [editingWord, setEditingWord] = useState<GlossaryWordListItem | null>(null);

  useEffect(() => {
    glossaryStore.getAll().catch(() => {
      notification.error({
        message: t('glossary:Notifications:fetchWordsFailed'),
        placement: 'bottomRight',
        duration: 3,
      });
    });
  }, [glossaryStore, t]);

  const { columnsMeta } = useGlossaryMeta({
    onEditClicked: (word) => {
      setEditingWord(word);
      setShowEditModal(true);
    },
    onDeleteClicked: (word) => {
      setDeletingWord(word);
      setShowDeleteModal(true);
    },
  });

  const onCloseDeleteModal = useCallback(() => setShowDeleteModal(false), []);

  const onDeleteWord = useCallback(async () => {
    if (!deletingWord) return;
    setDeleteInProgress(true);
    const res = await glossaryStore.delete(deletingWord.id);
    setDeleteInProgress(false);
    setShowDeleteModal(false);
    if (res.failed) {
      notification.error({
        message: t('glossary:Notifications:deleteFailed'),
        description: res.message,
        placement: 'bottomRight',
        duration: 3,
      });
    } else {
      notification.success({
        message: t('glossary:Notifications:deleteSuccess'),
        placement: 'bottomRight',
        duration: 2,
      });
    }
  }, [deletingWord, glossaryStore, t]);

  const onCloseEditModal = useCallback(() => {
    setShowEditModal(false);
    setEditingWord(null);
  }, []);

  const onSubmitEdit = useCallback(
    async (values: any, imageFile?: File | null) => {
      if (!editingWord) return;
      setEditInProgress(true);

      let imageUrl = editingWord.imageUrl || '';

      if (imageFile) {
        const uploadRes = await glossaryStore.uploadImage(editingWord.id, imageFile);
        if (!uploadRes.succeeded) {
          setEditInProgress(false);
          notification.error({
            message: t('glossary:Notifications:imageUploadFailed'),
            description: uploadRes.message,
            placement: 'bottomRight',
            duration: 3,
          });
          return;
        }
        imageUrl = uploadRes.data || '';
      }

      const res = await glossaryStore.update({ id: editingWord.id, ...values, imageUrl });
      setEditInProgress(false);
      if (res.succeeded) {
        setShowEditModal(false);
        setEditingWord(null);
        notification.success({
          message: t('glossary:Notifications:updateSuccess'),
          placement: 'bottomRight',
          duration: 2,
        });
      } else {
        notification.error({
          message: t('glossary:Notifications:updateFailed'),
          description: res.message,
          placement: 'bottomRight',
          duration: 3,
        });
      }
    },
    [editingWord, glossaryStore, t]
  );

  return (
    <ListContainer>
      <GdList
        split={false}
        itemMode={ItemMode.Card}
        data={glossaryStore.filteredWords as any}
        columnMeta={columnsMeta}
        totalCount={glossaryStore.filteredWords.length}
        loading={{
          spinning: glossaryStore.isLoading,
          size: 'large',
          indicator: <Spin indicator={<GdLoadingIndicator />} />,
        }}
      />

      <GdModal
        isVisible={showDeleteModal}
        disableButtons={isDeleteInProgress}
        isClosable={!isDeleteInProgress}
        onCloseModal={onCloseDeleteModal}
        onOkHandler={onDeleteWord}
        cancelButtonText={t('glossary:DeleteModal:cancelBtnLbl')}
        okButtonText={t('glossary:DeleteModal:deleteBtnLbl')}
        title={t('glossary:DeleteModal:title')}
        renderBody={
          <Text fontSize={16} fontFamily="montserrat-regular">
            {deletingWord &&
              t('glossary:DeleteModal:bodyConfirmationText', {
                word: deletingWord.word,
              })}
          </Text>
        }
      />

      <GlossaryWordModal
        isVisible={showEditModal}
        isLoading={isEditInProgress}
        editingWord={editingWord}
        onClose={onCloseEditModal}
        onSubmit={onSubmitEdit}
      />
    </ListContainer>
  );
});
